import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';

// The brief popout's composed body (phase-B §B1 / walk D3). Composed ON OPEN, per reader, from the
// clicked item's own note — nothing is added to the brief payload, so cost and latency land only on
// items someone actually opens.
//
// Sections travel as their note-doc `kind`; the reader-facing heading comes from the SPA's existing
// SECTION_LABELS (lib/note-doc.ts), which is the same vocabulary the note detail page already uses.
// `composed: false` means the server served the note's own lines uncomposed (its LLM failed or timed
// out) — still a real body, which is the point: the popout is never blank.

export interface BriefPopoutSection {
  kind: string;
  bullets: string[];
}

export interface BriefPopoutBodyData {
  conversationId: string;
  composed: boolean;
  standfirst: string | null;
  sections: BriefPopoutSection[];
}

export interface BriefPopoutRequest {
  conversationId: string;
  title?: string | null;
  tag?: string | null;
  why?: string | null;
  // How many citation rows the open dialog renders below the body. The server's one-screen budget
  // (bug-612/613) subtracts the citation block's height, and only the client knows the row count.
  // Absent/null falls back to the measured single-row block server-side.
  citationCount?: number | null;
}

export function useBriefPopoutBody(input: BriefPopoutRequest | null) {
  const query = useQuery({
    // Keyed on the conversation only: the same note opened from two cards is the same body, and the
    // card context (title/tag/why/citationCount) is a composition hint, not an identity.
    queryKey: ['surfaces', 'brief-popout', input?.conversationId ?? ''],
    queryFn: () =>
      apiFetch<BriefPopoutBodyData>('/api/surfaces/team/today/popout', {
        method: 'POST',
        body: JSON.stringify({
          conversationId: input!.conversationId,
          title: input?.title ?? null,
          tag: input?.tag ?? null,
          why: input?.why ?? null,
          citationCount: input?.citationCount ?? null,
        }),
      }),
    enabled: !!input?.conversationId,
    // A composed body is stable for the reader's session; re-opening the same card must not pay the
    // model again.
    staleTime: 30 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    // One attempt. A retry doubles the wait in front of an open dialog, and the dialog already has
    // an honest degraded state (the card's own description + item rows).
    retry: false,
  });
  return { ...query, data: query.data ?? undefined };
}
